/**
 * recommend_remediation — structural agent tool. Wraps the existing
 * recommend-fix endpoint that creates a KnowledgeChangeSet with an
 * LLM-drafted remediation. The user accepts/rejects via the existing
 * change-set inbox; this tool just kicks it off and returns the
 * change-set id + summary so the assistant can link the user to it.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';

function getFolderId(context: IAgentToolContext): string | null {
  const ctx = context.assistantContext as Record<string, unknown> | undefined;
  const f = ctx?.folderId;
  return typeof f === 'string' && f.length > 0 ? f : null;
}

interface RecommendResponse {
  changeSetId?: string;
  summary?: string;
  recommendation?: { body?: string };
}

export const recommendRemediationTool: IAgentTool = {
  name: 'recommend_remediation',
  description:
    'Generate an LLM-drafted remediation for a non-compliant control in the current folder. Creates a KnowledgeChangeSet the user can accept/reject in their inbox. Use when the user asks "how do I fix CC6.1" or "recommend a fix" for a specific control.',
  parameters: {
    type: 'object',
    properties: {
      controlId: { type: 'string', description: 'Catalog control id, e.g. "CC6.1".' },
      framework: { type: 'string', description: 'Framework id (default soc2).' },
    },
    required: ['controlId'],
  },
  supportedDiagramTypes: [],
  tier: 'structural',
  category: 'edit',

  async execute(params, context): Promise<AgentToolResult> {
    const folderId = getFolderId(context);
    if (!folderId) return { success: false, summary: 'No folder context available.' };
    const controlId = String(params.controlId ?? '');
    if (!controlId) return { success: false, summary: 'controlId is required.' };
    const framework = typeof params.framework === 'string' && params.framework ? params.framework : 'soc2';

    const url =
      `/api/compass/folders/${encodeURIComponent(folderId)}` +
      `/compliance/${encodeURIComponent(framework)}` +
      `/controls/${encodeURIComponent(controlId)}/recommend`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!response.ok) {
      return { success: false, summary: `Recommendation request failed (${response.status})` };
    }
    const body = (await response.json()) as RecommendResponse;
    const changeSetId = body.changeSetId ?? null;
    const draft = body.recommendation?.body ?? body.summary ?? '';
    return {
      success: true,
      summary:
        `Drafted a remediation for ${controlId}` +
        (changeSetId ? ` (change-set ${changeSetId})` : '') +
        '. Review in your change-set inbox to accept or reject.\n\n' +
        (draft.slice(0, 600) + (draft.length > 600 ? '…' : '')),
      data: { changeSetId, recommendation: draft },
      createdResourceIds: changeSetId ? [changeSetId] : [],
      uiSurface: 'chat',
    };
  },
};
